import { useCallback, useState } from "react";
import type { ClipUploadFile } from "./types";

const isVideoFile = (file: ClipUploadFile): boolean => !!file.type?.startsWith("video/");

// 视频预览尚未接入，除显式视频外一律按图片预览：
// 详情回填 / saved 等入口构造的历史图片可能不带 type，不能据此判定为不可预览。
const isImageFile = (file: ClipUploadFile): boolean => !isVideoFile(file);

export interface ClipUploadImagePreviewState {
  src: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface UseClipUploadPreviewResult {
  /** 返回卡片可绑定的预览触发函数；不支持预览时返回 undefined */
  getPreviewHandler: (file: ClipUploadFile) => (() => void) | undefined;
  /** 受控的图片预览状态，交给 ClipUploadImagePreview 渲染 */
  imagePreview: ClipUploadImagePreviewState;
}

/**
 * 负责 ClipUpload 已上传资源的预览状态与分发。
 * 视频预览后续接入时，只需在 getPreviewHandler 中返回新的触发函数。
 */
export const useClipUploadPreview = (previewUrls: Record<string, string>): UseClipUploadPreviewResult => {
  const [imageSrc, setImageSrc] = useState("");
  const [imageOpen, setImageOpen] = useState(false);

  const resolveUrl = useCallback(
    (file: ClipUploadFile): string | undefined => previewUrls[file.uid] ?? file.url ?? file.thumbUrl,
    [previewUrls],
  );

  const getPreviewHandler = useCallback(
    (file: ClipUploadFile): (() => void) | undefined => {
      if (isImageFile(file)) {
        const src = resolveUrl(file);
        if (!src) return undefined;
        return () => {
          setImageSrc(src);
          setImageOpen(true);
        };
      }
      // 视频预览入口待接入：后续在此处返回视频预览触发函数
      return undefined;
    },
    [resolveUrl],
  );

  const onOpenChange = useCallback((open: boolean) => {
    setImageOpen(open);
    if (!open) setImageSrc("");
  }, []);

  return {
    getPreviewHandler,
    imagePreview: { src: imageSrc, open: imageOpen, onOpenChange },
  };
};
